// covered-by: frontends/carrie/src/routes/tickets/__tests__/ticketCommentPaths.test.ts

export const TICKET_COMMENT_PATH = "/tickets/$ticketId/$boardId/$commentId";

export type TicketCommentParams = {
  ticketId: string;
  boardId: string;
  commentId: string;
};

export function buildTicketCommentPath(
  ticketId: string,
  boardId: string,
  commentId: string,
) {
  return {
    to: TICKET_COMMENT_PATH,
    params: { ticketId, boardId, commentId },
    search: {},
  } as const;
}

/** Server-side / notification path string (no TanStack `$` placeholders). */
export function ticketCommentPathString(
  ticketId: string,
  boardId: string,
  commentId: string,
): string {
  return `/tickets/${ticketId}/${boardId}/${commentId}`;
}

export function parseTicketCommentParams(
  params: Record<string, unknown>,
): TicketCommentParams | null {
  const ticketId =
    typeof params.ticketId === "string" ? params.ticketId.trim() : "";
  const boardId =
    typeof params.boardId === "string" ? params.boardId.trim() : "";
  const commentId =
    typeof params.commentId === "string" ? params.commentId.trim() : "";

  if (!ticketId || !boardId || !commentId) return null;

  return { ticketId, boardId, commentId };
}

/** Map a revision board id to its version index in sorted revisionBoards. */
export function boardIdToVersionIndex(
  revisionBoards: { _id?: string }[],
  boardId: string,
): number {
  const idx = revisionBoards.findIndex((board) => board._id === boardId);
  return idx >= 0 ? idx : 0;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * True when pathname is still this ticket's /view or comment deep-link.
 * Used to soft-leave between those sibling shells (Ask Puntt parity).
 */
export function isStillOnSameTicketShell(
  ticketId: string,
  pathname: string,
): boolean {
  const id = ticketId.trim();
  if (!id || !pathname) return false;

  const path = pathname.split("?")[0]?.split("#")[0] ?? "";
  const viewPath = `/tickets/${id}/view`;
  if (path === viewPath) return true;

  const commentPath = new RegExp(
    `^/tickets/${escapeRegExp(id)}/[^/]+/[^/]+/?$`,
  );
  return commentPath.test(path);
}
